/*
 * CallSupportButton — "tap to call MedexOVA" from inside the rider app.
 * No phone number to dial: mints a short-lived SignalWire Subscriber Access
 * Token server-side, registers a browser WebRTC client, and dials straight
 * into the SAME AI-triage line a phone caller reaches (`/api/calls/webrtc-token`
 * -> `voice_inbound` -> `triage_speech`). One communications pipeline, two
 * entry points.
 */
import React, { useEffect, useRef, useState } from "react";
import { Phone, PhoneOff, Loader2 } from "lucide-react";
import { toast } from "sonner";
import axios from "axios";

const API = process.env.REACT_APP_BACKEND_URL;

const STATUS_COPY = {
  idle: "Call MedexOVA dispatch — no dialing needed.",
  connecting: "Connecting…",
  dialing: "Ringing dispatch…",
  connected: "Connected — talk normally.",
  error: "Couldn't connect — try again.",
};

export default function CallSupportButton({ riderToken }) {
  const [status, setStatus] = useState("idle");
  const clientRef = useRef(null);
  const callRef = useRef(null);
  const remoteAudioRef = useRef(null);

  const cleanup = () => {
    try { callRef.current?.hangup?.(); } catch { /* noop */ }
    callRef.current = null;
    setStatus("idle");
  };

  useEffect(() => () => cleanup(), []);

  const startCall = async () => {
    if (!riderToken) return;
    setStatus("connecting");
    try {
      const { data } = await axios.post(`${API}/api/calls/webrtc-token`, {
        actor_type: "rider", actor_token: riderToken,
      });
      if (!data?.token) throw new Error("no token returned");

      const { SignalWire, StaticCredentialProvider } = await import("@signalwire/js");
      const client = new SignalWire(new StaticCredentialProvider({ token: data.token }));
      await client.register();
      clientRef.current = client;

      setStatus("dialing");
      const call = await client.dial(data.dial_to, { audio: true, video: false });
      callRef.current = call;

      call.remoteStream$?.subscribe((stream) => {
        if (remoteAudioRef.current) {
          remoteAudioRef.current.srcObject = stream;
          remoteAudioRef.current.play().catch(() => {});
        }
      });
      call.status$?.subscribe((s) => {
        if (s === "active" || s === "connected") setStatus("connected");
        if (s === "ended" || s === "destroy") cleanup();
      });

      setStatus("connected");
    } catch (e) {
      console.error(e);
      setStatus("error");
      toast.error("Couldn't start the call — try again in a moment.");
    }
  };

  return (
    <div data-testid="call-support-widget"
         className="rounded-2xl border border-[#E9EAE4] bg-white p-4 flex items-center justify-between gap-3">
      <div className="min-w-0">
        <div className="text-sm font-semibold text-slate_navy">Need help right now?</div>
        <div className="text-xs text-slate-500" data-testid="call-support-status">{STATUS_COPY[status]}</div>
      </div>
      {status === "connected" ? (
        <button data-testid="call-support-end-btn" onClick={cleanup}
                className="h-11 w-11 shrink-0 rounded-full bg-red-500 text-white grid place-items-center transition-transform active:scale-95">
          <PhoneOff className="h-5 w-5" />
        </button>
      ) : (
        <button data-testid="call-support-start-btn" onClick={startCall}
                disabled={status === "connecting" || status === "dialing"}
                className="h-11 w-11 shrink-0 rounded-full bg-forest text-white grid place-items-center disabled:opacity-60 transition-transform active:scale-95">
          {status === "connecting" || status === "dialing"
            ? <Loader2 className="h-5 w-5 animate-spin" />
            : <Phone className="h-5 w-5" />}
        </button>
      )}
      <audio ref={remoteAudioRef} autoPlay />
    </div>
  );
}
